import { FC, useState, useMemo, useCallback, useEffect } from 'react';
import { SortingState } from '@tanstack/react-table';
import { DataTable } from 'components/table/DataTable';
import { createPricingRulesColumns } from 'components/pricing/PricingRulesColumns';
import { PricingRulesFilters } from 'components/pricing/PricingRulesFilters';
import { EditRuleSheet } from 'components/pricing/EditRuleSheet';
import { usePricingRules } from 'src/hooks/usePricingRules';
import { PricingRule } from '@libs/schemas';
import { PricingFilters } from 'src/types/pricing';
import { useDebounce } from 'src/components/Debounce';

export const PricingRulesDataTable: FC = () => {
  // Pagination state
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(25);
  const [search, setSearch] = useState('');
  const [filters, setFilters] = useState<PricingFilters>({
    ruleType: 'all',
    status: 'all',
    scope: 'all',
  });
  const [sorting, setSorting] = useState<SortingState>([]);
  const [selectedRule, setSelectedRule] = useState<PricingRule | null>(null);
  const [editSheetOpen, setEditSheetOpen] = useState(false);

  // Debounce search to avoid excessive API calls
  const debouncedSearch = useDebounce(search, 500);

  // Reset page when debounced search changes
  useEffect(() => {
    setPage(1);
  }, [debouncedSearch]);

  // Convert filters to API format
  const apiFilters = useMemo(
    () => ({
      search: debouncedSearch || undefined,
      ruleType: filters.ruleType !== 'all' ? filters.ruleType : undefined,
      active: filters.status !== 'all' ? filters.status === 'active' : undefined,
    }),
    [debouncedSearch, filters],
  );

  const { rules, isLoading, deleteRule, duplicateRule, totalCount, hasNextPage } = usePricingRules({
    page,
    limit,
    ...apiFilters,
  });

  const handleEditRule = useCallback((rule: PricingRule) => {
    setSelectedRule(rule);
    setEditSheetOpen(true);
  }, []);

  const handleDeleteRule = useCallback(
    (rule: PricingRule) => {
      deleteRule(rule._id!);
    },
    [deleteRule],
  );

  const handleDuplicateRule = useCallback(
    (rule: PricingRule) => {
      duplicateRule(rule);
    },
    [duplicateRule],
  );

  const columns = useMemo(
    () => createPricingRulesColumns(handleEditRule, handleDeleteRule, handleDuplicateRule),
    [handleEditRule, handleDeleteRule, handleDuplicateRule],
  );

  const handleRowClick = (rule: PricingRule) => {
    handleEditRule(rule);
  };

  const handleEditSheetClose = (open: boolean) => {
    setEditSheetOpen(open);
    if (!open) {
      setSelectedRule(null);
    }
  };

  const handlePageChange = (direction: -1 | 1) => {
    setPage((prev) => Math.max(1, prev + direction));
  };

  const handleFiltersChange = (newFilters: PricingFilters) => {
    setFilters(newFilters);
    setPage(1); // Reset to first page when filters change
  };

  const handleSearchChange = (newSearch: string) => {
    setSearch(newSearch);
    // Don't reset page here - we'll do it when debounced value changes
  };

  const handlePageSizeChange = (newPageSize: number) => {
    setLimit(newPageSize);
    setPage(1); // Reset to first page when page size changes
  };

  const totalPages = Math.ceil(totalCount / limit);
  const hasPreviousPage = page > 1;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900 mx-auto mb-4"></div>
          <p>Loading pricing rules...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-6">
      <PricingRulesFilters
        search={search}
        onSearchChange={handleSearchChange}
        filters={filters}
        onFiltersChange={handleFiltersChange}
        pageSize={limit}
        onPageSizeChange={handlePageSizeChange}
      />

      <DataTable<PricingRule, unknown>
        columns={columns}
        data={rules}
        sorting={sorting}
        onSortingChange={setSorting}
        onRowClick={handleRowClick}
        hasNextPage={!!hasNextPage}
        hasPreviousPage={hasPreviousPage}
        onPageChange={handlePageChange}
        currentPage={page}
        pageCount={totalPages}
        total={totalCount}
        resourceName="pricing rules"
        offset={(page - 1) * limit}
        limit={limit}
      />

      {selectedRule && (
        <EditRuleSheet
          rule={selectedRule}
          open={editSheetOpen}
          onOpenChange={handleEditSheetClose}
        />
      )}
    </div>
  );
};
